import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import useI18n from '../hooks/useI18n';
import { trackFeature } from '../services/telemetryService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faTimes, 
  faChartBar,
  faFolder,
  faBox,
  faDollarSign,
  faChartLine,
  faSearch,
  faInfoCircle,
  faCopy,
  faExternalLinkAlt,
  faTag,
  faStar,
  faShieldAlt,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { api, resolveFileUrl } from '../services/api';
import useCurrency from '../hooks/useCurrency';
import { availableIcons, iconMap } from '../utils/iconList';
import '../styles/Categories.css';

interface Category {
  id: string;
  name: string;
  description?: string;
  itemCount?: number;
  totalValue?: number;
  avgValue?: number;
  color: string;
  icon?: string;
  isDefault?: boolean;
  createdAt?: string;
}

interface FormData {
  name: string;
  description: string;
  color: string;
  icon?: string;
}

const Categories = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { t } = useI18n();
  const { getCurrencySymbol } = useCurrency();

  // Translate default category names & descriptions
  const tc = (name: string) => {
    const key = `categoryName.${name}` as any;
    const translated = t(key);
    return translated !== key ? translated : name;
  };
  const td = (desc: string) => {
    const key = `categoryDesc.${desc}` as any;
    const translated = t(key);
    return translated !== key ? translated : desc;
  };

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({ name: '', description: '', color: '#667eea' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'items' | 'created'>('name');
  const [showStats, setShowStats] = useState(false);
  const [iconSearchTerm, setIconSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categoryItems, setCategoryItems] = useState<any[]>([]);
  const [loadingCategoryItems, setLoadingCategoryItems] = useState(false);
  const [closing, setClosing] = useState(false);

  const defaultColors = [
    '#667eea', '#764ba2', '#f093fb', '#f5576c', '#11998e',
    '#38ef7d', '#4facfe', '#00c2ff', '#fbc531', '#fa709a'
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCategories();
    } else {
      setLoading(false);
    }
  }, []);

  // Lock body scroll to avoid layout shift when modals open
  useEffect(() => {
    const isModalOpen = showForm || !!selectedCategory;
    if (!isModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
    };
  }, [showForm, selectedCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      console.log('Fetching categories...');
      const response = await api.get('/categories');
      console.log('Categories response:', response.data);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid categories response format');
        setCategories([]);
        return;
      }

      setCategories(response.data.map((cat: any) => ({
        ...cat,
        color: cat.color || '#667eea',
        itemCount: cat.stats?.itemCount || cat._count?.items || 0,
        totalValue: cat.stats?.totalValue || 0,
        avgValue: cat.stats?.avgValue || 0,
      })));
      console.log('Categories set successfully');
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      alert('Failed to load categories. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (category?: Category) => {
    if (category) {
      setEditingId(category.id);
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color,
        icon: category.icon || 'faBox',
      });
    } else {
      setEditingId(null);
      setFormData({ name: '', description: '', color: '#667eea', icon: 'faBox' });
    }
    setShowForm(true);
  };
  const handleCloseForm = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', description: '', color: '#667eea', icon: 'faBox' });
      setIconSearchTerm('');
    }, 200);
  }, []);

  const handleSaveCategory = async () => {
    if (!formData.name.trim()) {
      alert('Category name is required');
      return;
    }

    try {
      if (editingId) {
        // Update existing category
        const response = await api.put(`/categories/${editingId}`, {
          name: formData.name,
          description: formData.description || null,
          color: formData.color,
          icon: formData.icon,
        });
        setCategories(categories.map(cat => cat.id === editingId ? { ...response.data, itemCount: cat.itemCount || 0 } : cat));
        trackFeature('category_updated', { categoryId: editingId });
      } else {
        // Create new category
        const response = await api.post('/categories', {
          name: formData.name,
          description: formData.description || null,
          color: formData.color,
          icon: formData.icon,
        });
        setCategories([...categories, { ...response.data, itemCount: 0 }]);
        trackFeature('category_created', { categoryId: response.data.id });
      }
      handleCloseForm();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert('Failed to save category');
    }
  };

  const handleDeleteCategory = async (id: string, isDefault?: boolean, itemCount?: number) => {
    // Show appropriate warning based on category type and item count
    let confirmMessage = t('categories.confirmDelete');
    
    if (itemCount && itemCount > 0) {
      alert(t('categories.cannotDeleteWithItems').replace('{count}', String(itemCount)));
      return;
    }
    
    if (isDefault) {
      confirmMessage = t('categories.confirmDeleteDefault');
    }

    if (window.confirm(confirmMessage)) {
      try {
        await api.delete(`/categories/${id}`);
        setCategories(categories.filter(cat => cat.id !== id));
        trackFeature('category_deleted', { categoryId: id });
      } catch (error: any) {
        console.error('Failed to delete category:', error);
        const errorMessage = error.response?.data?.error || 'Failed to delete category';
        alert(errorMessage);
      }
    }
  };

  const handleDuplicateCategory = async (id: string) => {
    try {
      const response = await api.post(`/categories/${id}/duplicate`);
      setCategories([...categories, { ...response.data, itemCount: 0, totalValue: 0, avgValue: 0 }]);
    } catch (error) {
      console.error('Failed to duplicate category:', error);
      alert('Failed to duplicate category');
    }
  };

  const handleOpenCategoryDetails = async (category: Category) => {
    setSelectedCategory(category);
    setLoadingCategoryItems(true);
    try {
      const response = await api.get('/items', { params: { categoryId: category.id, take: 100 } });
      setCategoryItems(response.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch category items:', error);
      setCategoryItems([]);
    } finally {
      setLoadingCategoryItems(false);
    }
  };

  const handleCloseCategoryDetails = () => {
    setSelectedCategory(null);
    setCategoryItems([]);
  };

  // Filter and sort categories
  const filteredAndSortedCategories = categories
    .filter(cat => 
      cat && cat.id && cat.name && (
        cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cat.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'items':
          return (b.itemCount || 0) - (a.itemCount || 0);
        case 'created':
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        default:
          return 0;
      }
    });

  // Calculate overall statistics
  const totalCategories = categories.length;
  const totalItems = categories.reduce((sum, cat) => sum + (cat.itemCount || 0), 0);
  const totalValue = categories.reduce((sum, cat) => sum + (cat.totalValue || 0), 0);

  if (loading) {
    return (
      <div className="categories" style={{ padding: '60px', textAlign: 'center', color: '#a0aec0' }}>
        <FontAwesomeIcon icon={faBox} style={{ marginRight: '8px' }} />
        {t('categories.loading')}
      </div>
    );
  }

  return (
    <div className="categories">
      <div className="categories-header">
        <div>
          <h1>{t('categories.title')}</h1>
          <p className="header-subtitle">{t('categories.subtitle')}</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn-stats"
            onClick={() => setShowStats(!showStats)}
            title="Toggle statistics"
          >
            <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '8px' }} />
            {showStats ? t('categories.hideStats') : t('categories.showStats')}
          </button>
          <button 
            className="btn-add-category"
            onClick={() => handleOpenForm()}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t('categories.newCategory')}
          </button>
        </div>
      </div>

      {/* Statistics Panel */}
      {showStats && (
        <div className="stats-panel">
          <div className="stat-card">
            <div className="stat-icon"><FontAwesomeIcon icon={faFolder} /></div>
            <div className="stat-content">
              <div className="stat-value">{totalCategories}</div>
              <div className="stat-label">{t('categories.categories')}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FontAwesomeIcon icon={faBox} /></div>
            <div className="stat-content">
              <div className="stat-value">{totalItems}</div>
              <div className="stat-label">{t('categories.totalItems')}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FontAwesomeIcon icon={faDollarSign} /></div>
            <div className="stat-content">
              <div className="stat-value">${totalValue.toLocaleString()}</div>
              <div className="stat-label">{t('categories.totalValue')}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FontAwesomeIcon icon={faChartLine} /></div>
            <div className="stat-content">
              <div className="stat-value">{totalItems > 0 ? (totalValue / totalItems).toFixed(2) : '0'}</div>
              <div className="stat-label">{t('categories.avgValuePerItem')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Sort Controls */}
      <div className="categories-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('categories.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon"><FontAwesomeIcon icon={faSearch} /></span>
        </div>
        <div className="sort-controls">
          <label>{t('categories.sortBy')}:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value as any)}
            className="sort-select"
          >
            <option value="name">{t('categories.name')}</option>
            <option value="items">{t('categories.itemCount')}</option>
            <option value="created">{t('categories.dateCreated')}</option>
          </select>
        </div>
      </div>

      {/* Category Form Modal */}
      {showForm && (
        <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleCloseForm}>
          <div className={`category-form-modal ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingId 
                  ? (categories.find(c => c.id === editingId)?.isDefault 
                      ? t('categories.customizeDefault') 
                      : t('categories.editCategory')
                    )
                  : t('categories.createNew')
                }
              </h3>
              <button className="modal-close" onClick={handleCloseForm}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              {editingId && categories.find(c => c.id === editingId)?.isDefault && (
                <div className="info-banner">
                  <span className="info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  <p>{t('categories.infoBanner')}</p>
                </div>
              )}
              <div className="form-group">
                <label>{t('categories.categoryName')}</label>
                <input
                  type="text"
                  placeholder={t('categories.enterName')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>{t('categories.description')}</label>
                <textarea
                  placeholder={t('categories.describeCategory')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>{t('categories.color')}</label>
                <div className="color-picker">
                  {defaultColors.map(color => (
                    <button
                      key={color}
                      className={`color-option ${formData.color === color ? 'active' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData({ ...formData, color })}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>{t('categories.icon')}</label>
                <input
                  type="text"
                  placeholder={t('categories.searchIcons')}
                  value={iconSearchTerm}
                  onChange={(e) => setIconSearchTerm(e.target.value.toLowerCase())}
                  className="icon-search-input"
                />
                <div className="icon-picker">
                  {availableIcons
                    .filter(iconOption => 
                      iconOption.name.toLowerCase().includes(iconSearchTerm) ||
                      iconOption.key.toLowerCase().includes(iconSearchTerm)
                    )
                    .map(iconOption => (
                    <button
                      key={iconOption.key}
                      className={`icon-option ${formData.icon === iconOption.key ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, icon: iconOption.key })}
                      title={iconOption.name}
                    >
                      <FontAwesomeIcon icon={iconOption.icon} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button className="btn-primary" onClick={handleSaveCategory}>
                  {editingId ? t('categories.updateCategory') : t('categories.createCategory')}
                </button>
                <button className="btn-secondary" onClick={handleCloseForm}>
                  {t('categories.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories Grid */}
      <div className="categories-grid">
        {filteredAndSortedCategories.map(category => (
          <div 
            key={category.id} 
            className="category-card"
            style={{ borderTopColor: category.color }}
            onClick={() => handleOpenCategoryDetails(category)}
          >
            <div className="category-header-row">
              <div className="category-icon" style={{ background: category.color }}>
                <FontAwesomeIcon icon={category.icon && iconMap[category.icon] ? iconMap[category.icon] : faFolder} />
              </div>
              <div className="category-actions-quick">
                <button 
                  className="action-btn-quick duplicate" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicateCategory(category.id);
                  }}
                  title="Duplicate category"
                >
                  <FontAwesomeIcon icon={faCopy} />
                </button>
                <button 
                  className="action-btn-quick edit" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenForm(category);
                  }}
                  title={category.isDefault ? "Customize default category" : "Edit category"}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button 
                  className="action-btn-quick delete" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCategory(category.id, category.isDefault, category.itemCount);
                  }}
                  title={category.itemCount ? `Cannot delete (${category.itemCount} items)` : "Delete category"}
                  disabled={!!(category.itemCount && category.itemCount > 0)}
                  style={{ 
                    opacity: category.itemCount && category.itemCount > 0 ? 0.5 : 1, 
                    cursor: category.itemCount && category.itemCount > 0 ? 'not-allowed' : 'pointer' 
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            <div className="category-content">
              <h4>{tc(category.name)}</h4>
              {category.isDefault && <span className="badge-default">{t('categories.default')}</span>}
              <p className="category-description">{category.description ? td(category.description) : t('categories.noDescription')}</p>
              <div className="category-stats">
                <div className="stat-item">
                  <span className="stat-icon"><FontAwesomeIcon icon={faBox} /></span>
                  <span className="stat-text">{category.itemCount || 0} {t('categories.items')}</span>
                </div>
                {category.totalValue && category.totalValue > 0 && (
                  <>
                    <div className="stat-item">
                      <span className="stat-icon"><FontAwesomeIcon icon={faDollarSign} /></span>
                      <span className="stat-text">{getCurrencySymbol(user?.defaultCurrency || 'USD')}{category.totalValue.toLocaleString()}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-icon"><FontAwesomeIcon icon={faChartBar} /></span>
                      <span className="stat-text">{t('categories.avg')} {getCurrencySymbol(user?.defaultCurrency || 'USD')}{category.avgValue?.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredAndSortedCategories.length === 0 && !loading && (
        <div className="empty-state">
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>
            <FontAwesomeIcon icon={searchTerm ? faSearch : faFolder} />
          </div>
          <h3>{searchTerm ? t('categories.noCategoriesFound') : t('categories.noCategories')}</h3>
          <p>{searchTerm ? t('categories.tryDifferent') : t('categories.createToOrganize')}</p>
        </div>
      )}

      {selectedCategory && (
        <div className="modal-overlay" onClick={handleCloseCategoryDetails}>
          <div className="category-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{tc(selectedCategory.name)}</h3>
              <button className="modal-close" onClick={handleCloseCategoryDetails}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p className="category-detail-description">
                {selectedCategory.description ? td(selectedCategory.description) : t('categories.noDescription')}
              </p>

              {/* Enhanced Stats Grid */}
              <div className="category-detail-stats-grid">
                <div className="detail-stat-card">
                  <FontAwesomeIcon icon={faBox} className="detail-stat-icon" />
                  <div className="detail-stat-value">{selectedCategory.itemCount || 0}</div>
                  <div className="detail-stat-label">{t('categories.items')}</div>
                </div>
                <div className="detail-stat-card">
                  <FontAwesomeIcon icon={faDollarSign} className="detail-stat-icon" />
                  <div className="detail-stat-value">
                    {getCurrencySymbol(user?.defaultCurrency || 'USD')}
                    {(selectedCategory.totalValue || 0).toLocaleString()}
                  </div>
                  <div className="detail-stat-label">{t('categories.totalValue')}</div>
                </div>
                <div className="detail-stat-card">
                  <FontAwesomeIcon icon={faChartLine} className="detail-stat-icon" />
                  <div className="detail-stat-value">
                    {getCurrencySymbol(user?.defaultCurrency || 'USD')}
                    {(selectedCategory.avgValue || 0).toFixed(0)}
                  </div>
                  <div className="detail-stat-label">{t('categories.avgValuePerItem')}</div>
                </div>
                {categoryItems.length > 0 && (() => {
                  const mostValuable = [...categoryItems].filter(i => !i.isLocked).sort((a, b) => (b.purchasePrice || 0) - (a.purchasePrice || 0))[0];
                  return mostValuable?.purchasePrice ? (
                    <div className="detail-stat-card highlight" onClick={() => navigate(`/library?item=${mostValuable.id}`)} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faStar} className="detail-stat-icon" />
                      <div className="detail-stat-value">
                        {getCurrencySymbol(user?.defaultCurrency || 'USD')}{mostValuable.purchasePrice.toLocaleString()}
                      </div>
                      <div className="detail-stat-label">{t('categories.mostValuable')}</div>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Condition Distribution */}
              {categoryItems.length > 0 && (() => {
                const condMap: Record<string, number> = {};
                categoryItems.forEach(item => {
                  const c = item.condition || 'N/A';
                  condMap[c] = (condMap[c] || 0) + 1;
                });
                const condLabels: Record<string, string> = {
                  'NEW': t('condition.new'), 'LIKE_NEW': t('condition.likeNew'),
                  'EXCELLENT': t('condition.excellent'), 'GOOD': t('condition.good'),
                  'FAIR': t('condition.fair'), 'POOR': t('condition.poor'), 'N/A': 'N/A'
                };
                const condColors: Record<string, string> = {
                  'NEW': '#22c55e', 'LIKE_NEW': '#10b981', 'EXCELLENT': '#06b6d4',
                  'GOOD': '#3b82f6', 'FAIR': '#f59e0b', 'POOR': '#ef4444', 'N/A': '#6b7280'
                };
                return (
                  <div className="condition-distribution">
                    <h5><FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 6 }} /> {t('categories.conditionDistribution')}</h5>
                    <div className="condition-bars">
                      {Object.entries(condMap).sort((a, b) => b[1] - a[1]).map(([cond, count]) => (
                        <div key={cond} className="condition-bar-row">
                          <span className="condition-bar-label">{condLabels[cond] || cond}</span>
                          <div className="condition-bar-track">
                            <div 
                              className="condition-bar-fill" 
                              style={{ 
                                width: `${(count / categoryItems.length) * 100}%`,
                                background: condColors[cond] || '#6b7280'
                              }} 
                            />
                          </div>
                          <span className="condition-bar-count">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="category-detail-items">
                <div className="category-detail-items-header">
                  <h4>{t('categories.recentItems')}</h4>
                  {(selectedCategory.itemCount || 0) > 0 && (
                    <button 
                      className="btn-view-all-in-library"
                      onClick={() => {
                        handleCloseCategoryDetails();
                        navigate(`/library?categoryId=${selectedCategory.id}`);
                      }}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} /> {t('categories.viewAllInLibrary')}
                    </button>
                  )}
                </div>
                {loadingCategoryItems ? (
                  <div className="category-detail-loading">{t('categories.loadingItems')}</div>
                ) : (
                  <div className="category-detail-grid">
                    {categoryItems.length > 0 ? (
                      categoryItems.slice(0, 6).map((item) => (
                        <div key={item.id} className="category-detail-item" 
                          onClick={() => navigate(`/library?item=${item.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="category-detail-thumb">
                            {item.isLocked ? (
                              <FontAwesomeIcon icon={faLock} style={{ color: '#f59e0b' }} />
                            ) : item.imageUrl ? (
                              <img src={resolveFileUrl(item.imageUrl) || undefined} alt={item.name} />
                            ) : (
                              <FontAwesomeIcon icon={faBox} />
                            )}
                          </div>
                          <div className="category-detail-info">
                            <div className="category-detail-name">
                              {item.isLocked && <FontAwesomeIcon icon={faLock} style={{ color: '#f59e0b', marginRight: 6, fontSize: '0.8em' }} />}
                              {item.isLocked ? t('itemDetail.locked') : item.name}
                            </div>
                            {!item.isLocked && item.purchasePrice && (
                              <div className="category-detail-price">
                                {getCurrencySymbol(user?.defaultCurrency || 'USD')}{item.purchasePrice}
                              </div>
                            )}
                            {!item.isLocked && item.condition && item.condition !== 'N/A' && (
                              <div className="category-detail-condition">
                                <FontAwesomeIcon icon={faTag} style={{ fontSize: '0.7em', marginRight: 4 }} />
                                {({
                                  'NEW': t('condition.new'), 'LIKE_NEW': t('condition.likeNew'),
                                  'EXCELLENT': t('condition.excellent'), 'GOOD': t('condition.good'),
                                  'FAIR': t('condition.fair'), 'POOR': t('condition.poor'),
                                } as Record<string, string>)[item.condition] || item.condition}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="category-detail-empty">{t('categories.noItemsInCategory')}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
